// Scritture sull'ordine WooCommerce dalla scheda ordine: cambio stato, note, spedizione.
// Passano da NIL (docs/woocommerce.md, "Scritture sugli ordini"). ATTENZIONE: alcune fanno
// partire una MAIL AL CLIENTE dal sito: qui lo diciamo sempre prima della conferma e mostriamo
// il message di NIL dopo. L'invio al cliente non ha mai un default: l'utente deve scegliere.
import {useEffect, useState} from 'react';
import {Caricamento} from '../Layout';
import {
    STATI_ORDINE_FILTRO, StatoOrdine, aggiungiNotaOrdine, cambiaStatoOrdine, dataOra, getNoteOrdine,
    getSpedizioneOrdine, mailPerCambioStato, oggi, salvaSpedizioneOrdine, testoErroreScrittura,
} from '../woo';

// Esito di una scrittura: message di NIL (verde) o errore (rosso).
function Esito({esito}) {
    if (!esito) return null;
    return (
        <div className={`alert alert-${esito.errore ? 'danger' : 'success'} small mb-3`}>
            {esito.mail && <i className="bi-envelope-check me-2" title="E' partita una mail al cliente"></i>}
            {esito.testo}
        </div>
    );
}

// Scelta obbligatoria: nota privata oppure inviata al cliente via mail. Nessun default.
function SceltaInvio({nome, valore, onChange}) {
    return (
        <div>
            <div className="form-check">
                <input className="form-check-input" type="radio" id={`${nome}-priv`} name={nome}
                       checked={valore === false} onChange={() => onChange(false)}/>
                <label className="form-check-label" htmlFor={`${nome}-priv`}>Nota privata (solo amministrazione, nessuna mail)</label>
            </div>
            <div className="form-check">
                <input className="form-check-input" type="radio" id={`${nome}-cli`} name={nome}
                       checked={valore === true} onChange={() => onChange(true)}/>
                <label className="form-check-label text-danger" htmlFor={`${nome}-cli`}>
                    <i className="bi-envelope-exclamation me-1"></i>Invia al cliente via mail
                </label>
            </div>
        </div>
    );
}

// ---------- cambio stato ----------

export function CambioStato({ordine, onOrdineAggiornato, onNoteCambiate}) {
    const [stato, setStato] = useState(ordine.status);
    const [nota, setNota] = useState('');
    const [inCorso, setInCorso] = useState(false);
    const [esito, setEsito] = useState(null);

    useEffect(() => setStato(ordine.status), [ordine.status]);

    const mail = mailPerCambioStato(ordine.status, stato);
    const uguale = stato === ordine.status;
    const etichetta = (s) => STATI_ORDINE_FILTRO.find((x) => x.valore === s)?.etichetta ?? s;

    function conferma(event) {
        event.preventDefault();
        let testo = `Cambiare lo stato dell'ordine #${ordine.number} da "${etichetta(ordine.status)}" a "${etichetta(stato)}"?\n\n`;
        testo += mail
            ? `ATTENZIONE: il sito invierà al cliente la mail "${mail}".`
            : 'Nessuna mail al cliente.';
        if (stato === 'refunded') testo += '\n\nWooCommerce creerà anche un rimborso contabile per l\'importo residuo dell\'ordine.';
        if (nota.trim()) testo += '\n\nVerrà aggiunta una nota privata (non inviata al cliente).';
        if (!window.confirm(testo)) return;

        setInCorso(true);
        setEsito(null);
        cambiaStatoOrdine(ordine.id, stato, nota.trim())
            .then((res) => {
                setEsito({testo: res.messaggio, mail: res.mail_cliente});
                setNota('');
                onOrdineAggiornato(res.ordine);
                if (res.nota) onNoteCambiate();
            })
            .catch((err) => {
                const t = testoErroreScrittura(err);
                if (t) setEsito({errore: true, testo: t});
                if (/nota NON/i.test(err.message)) onOrdineAggiornato({...ordine, status: stato});
            })
            .finally(() => setInCorso(false));
    }

    return (
        <div className="card h-100">
            <div className="card-body">
                <h6 className="card-title">Stato ordine</h6>
                <div className="mb-3">Attuale: <StatoOrdine stato={ordine.status}/></div>
                <Esito esito={esito}/>
                <form onSubmit={conferma}>
                    <div className="row g-2 align-items-end">
                        <div className="col-12 col-sm-6">
                            <label className="form-label" htmlFor="nuovo-stato">Nuovo stato</label>
                            <select id="nuovo-stato" className="form-select" value={stato} onChange={(e) => setStato(e.target.value)}>
                                {STATI_ORDINE_FILTRO.filter((s) => s.valore !== 'checkout-draft')
                                    .map((s) => <option key={s.valore} value={s.valore}>{s.etichetta}</option>)}
                            </select>
                        </div>
                        <div className="col-12 col-sm-6">
                            <button type="submit" className="btn btn-primary w-100" disabled={uguale || inCorso}>
                                {inCorso && <span className="spinner-border spinner-border-sm me-2" role="status"></span>}
                                Cambia stato
                            </button>
                        </div>
                        <div className="col-12">
                            <input type="text" className="form-control" placeholder="Nota privata facoltativa (non inviata al cliente)"
                                   value={nota} onChange={(e) => setNota(e.target.value)} maxLength={500}/>
                        </div>
                    </div>
                    {!uguale && (
                        <div className={`small mt-2 ${mail ? 'text-danger fw-semibold' : 'text-secondary'}`}>
                            {mail
                                ? <><i className="bi-envelope-exclamation me-1"></i>Il sito invierà al cliente la mail "{mail}".</>
                                : 'Nessuna mail al cliente.'}
                            {stato === 'refunded' && ' WooCommerce creerà anche un rimborso contabile.'}
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
}

// ---------- note ----------

export function NoteOrdine({ordine, versione}) {
    const [note, setNote] = useState(null); // null = in caricamento
    const [errore, setErrore] = useState('');
    const [testo, setTesto] = useState('');
    const [alCliente, setAlCliente] = useState(null); // scelta obbligatoria
    const [inCorso, setInCorso] = useState(false);
    const [esito, setEsito] = useState(null);

    // versione cambia quando un'altra scrittura (stato, spedizione) ha creato una nota.
    useEffect(() => {
        setErrore('');
        getNoteOrdine(ordine.id)
            .then((r) => setNote(r.note ?? []))
            .catch((err) => {
                const t = testoErroreScrittura(err);
                if (t) setErrore(t);
                setNote([]);
            });
    }, [ordine.id, versione]);

    function invia(event) {
        event.preventDefault();
        if (alCliente === null || !testo.trim()) return;
        if (alCliente && !window.confirm(`ATTENZIONE: il sito invierà questa nota al cliente via mail:\n\n"${testo.trim()}"\n\nConfermi?`)) return;

        setInCorso(true);
        setEsito(null);
        aggiungiNotaOrdine(ordine.id, testo.trim(), alCliente)
            .then((res) => {
                setEsito({testo: res.messaggio, mail: res.mail_cliente});
                setNote((prev) => [res.nota, ...(prev ?? [])]);
                setTesto('');
                setAlCliente(null);
            })
            .catch((err) => {
                const t = testoErroreScrittura(err);
                if (t) setEsito({errore: true, testo: t});
            })
            .finally(() => setInCorso(false));
    }

    return (
        <div className="card mb-3">
            <div className="card-body">
                <h6 className="card-title">Note dell'ordine</h6>
                {errore && <div className="alert alert-danger small">{errore}</div>}
                {note === null && <Caricamento/>}
                {note?.length === 0 && !errore && <p className="text-secondary">Nessuna nota.</p>}
                {note?.length > 0 && (
                    <ul className="list-unstyled mb-3">
                        {note.map((n) => (
                            <li key={n.id} className={`nota-ordine ${n.al_cliente ? 'nota-cliente' : ''}`}>
                                <div className="small text-secondary">
                                    {dataOra(n.date_created)} · {n.author}
                                    {n.al_cliente
                                        ? <span className="badge text-bg-danger ms-2"><i className="bi-envelope me-1"></i>inviata al cliente</span>
                                        : <span className="badge text-bg-light ms-2">privata</span>}
                                </div>
                                <div style={{whiteSpace: 'pre-wrap'}}>{n.nota}</div>
                            </li>
                        ))}
                    </ul>
                )}
                <Esito esito={esito}/>
                <form onSubmit={invia}>
                    <textarea className="form-control mb-2" rows={2} placeholder="Nuova nota" value={testo}
                              onChange={(e) => setTesto(e.target.value)} maxLength={2000} required/>
                    <div className="d-flex flex-wrap align-items-end gap-3">
                        <SceltaInvio nome="nota-invio" valore={alCliente} onChange={setAlCliente}/>
                        <button type="submit" className="btn btn-primary ms-auto" disabled={alCliente === null || !testo.trim() || inCorso}>
                            {inCorso && <span className="spinner-border spinner-border-sm me-2" role="status"></span>}
                            Aggiungi nota
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// ---------- spedizione ----------

export function SpedizioneOrdine({ordine, onNoteCambiate}) {
    const [spedizione, setSpedizione] = useState(undefined); // undefined = in caricamento, null = assente
    const [errore, setErrore] = useState('');
    const [form, setForm] = useState({corriere: '', numero: '', url: '', data: oggi()});
    const [alCliente, setAlCliente] = useState(null);
    const [inCorso, setInCorso] = useState(false);
    const [esito, setEsito] = useState(null);

    useEffect(() => {
        setErrore('');
        getSpedizioneOrdine(ordine.id)
            .then((r) => {
                setSpedizione(r.spedizione);
                setForm({corriere: r.spedizione.corriere, numero: r.spedizione.numero, url: r.spedizione.url ?? '', data: r.spedizione.data_spedizione || oggi()});
            })
            .catch((err) => {
                if (err.status === 404) return setSpedizione(null); // nessun tracking: non e' un errore
                const t = testoErroreScrittura(err);
                if (t) setErrore(t);
                setSpedizione(null);
            });
    }, [ordine.id]);

    function salva(event) {
        event.preventDefault();
        if (alCliente === null) return;
        let testo = `${spedizione ? 'Sostituire' : 'Salvare'} la spedizione dell'ordine #${ordine.number}: ${form.corriere} ${form.numero}?\n\n`;
        testo += alCliente
            ? 'ATTENZIONE: il sito invierà al cliente una mail con corriere, numero di tracking e link.'
            : 'Nessuna mail al cliente (nota privata).';
        testo += '\n\nLo stato dell\'ordine non cambia.';
        if (!window.confirm(testo)) return;

        setInCorso(true);
        setEsito(null);
        salvaSpedizioneOrdine(ordine.id, {
            corriere: form.corriere.trim(), numero: form.numero.trim(), url: form.url.trim() || undefined,
            data: form.data || undefined, invia_al_cliente: alCliente,
        })
            .then((res) => {
                setEsito({testo: res.messaggio, mail: res.mail_cliente});
                setSpedizione(res.spedizione);
                setForm((f) => ({...f, url: res.spedizione.url ?? ''}));
                setAlCliente(null);
                onNoteCambiate();
            })
            .catch((err) => {
                const t = testoErroreScrittura(err);
                if (t) setEsito({errore: true, testo: t});
            })
            .finally(() => setInCorso(false));
    }

    const campo = (k) => (e) => setForm({...form, [k]: e.target.value});

    return (
        <div className="card h-100">
            <div className="card-body">
                <h6 className="card-title">Spedizione</h6>
                {errore && <div className="alert alert-danger small">{errore}</div>}
                {spedizione === undefined && <Caricamento/>}
                {spedizione === null && <p className="text-secondary">Nessun tracking salvato.</p>}
                {spedizione && (
                    <div className="mb-3">
                        <div className="fw-semibold">{spedizione.corriere} {spedizione.numero}</div>
                        {spedizione.data_spedizione && <div className="small text-secondary">Spedito il {dataOra(spedizione.data_spedizione)}</div>}
                        {spedizione.url
                            ? <a href={spedizione.url} target="_blank" rel="noreferrer"><i className="bi-box-arrow-up-right me-1"></i>Traccia la spedizione</a>
                            : <span className="small text-secondary">Nessun link di tracking.</span>}
                    </div>
                )}
                <Esito esito={esito}/>
                {spedizione !== undefined && (
                    <form onSubmit={salva}>
                        <div className="row g-2 mb-2">
                            <div className="col-6">
                                <input type="text" className="form-control" placeholder="Corriere (es. BRT)" value={form.corriere}
                                       onChange={campo('corriere')} required list="corrieri"/>
                                <datalist id="corrieri">
                                    {['BRT', 'GLS', 'DHL', 'UPS', 'Poste', 'SDA', 'FedEx', 'TNT'].map((c) => <option key={c} value={c}/>)}
                                </datalist>
                            </div>
                            <div className="col-6">
                                <input type="text" className="form-control" placeholder="Numero spedizione" value={form.numero}
                                       onChange={campo('numero')} required/>
                            </div>
                            <div className="col-6">
                                <input type="date" className="form-control" value={form.data} onChange={campo('data')}/>
                            </div>
                            <div className="col-6">
                                <input type="url" className="form-control" placeholder="Link tracking (facoltativo)" value={form.url}
                                       onChange={campo('url')}/>
                            </div>
                        </div>
                        <p className="small text-secondary mb-2">Senza link, per i corrieri noti lo costruisce NIL. Lo stato dell'ordine non cambia: usa "Stato ordine".</p>
                        <div className="d-flex flex-wrap align-items-end gap-3">
                            <SceltaInvio nome="sped-invio" valore={alCliente} onChange={setAlCliente}/>
                            <button type="submit" className="btn btn-primary ms-auto"
                                    disabled={alCliente === null || !form.corriere.trim() || !form.numero.trim() || inCorso}>
                                {inCorso && <span className="spinner-border spinner-border-sm me-2" role="status"></span>}
                                {spedizione ? 'Sostituisci spedizione' : 'Salva spedizione'}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}
